import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { LucideAngularModule, ChevronDown, VideoOff, MicOff } from 'lucide-angular';
import { CallKind } from '../../../core/models/call.model';
import { SalleAppel } from '../../../core/services/salle-appel.service';
import { PhotoAppelComponent } from './photo-appel.component';
import { TranslationService } from '../../../core/services/translation.service';
import { BarreControlesAppelComponent } from './barre-controles-appel.component';
import { PisteVideoComponent } from './piste-video.component';
import { TuileAppel } from './tuiles-appel';

/**
 * Appel actif a deux — maquette `Main` (Ecrans d'appel Konbit POS v2).
 *
 * ABSENT DE LA MAQUETTE, VOLONTAIREMENT (aucune fonctionnalite derriere) : les reactions
 * rapides (coeur, pouce, applaudir), le bouton de discussion pendant l'appel, le menu « Plus
 * d'options » et la ligne « Caisse principale ». Un bouton sans effet est pire qu'un bouton
 * absent. Le bandeau de participants est dans l'ecran de groupe (3 personnes ou plus).
 */
@Component({
  selector: 'app-ecran-appel-actif',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LucideAngularModule, BarreControlesAppelComponent, PisteVideoComponent, PhotoAppelComponent],
  template: `
    <div class="fixed inset-0 z-50 overflow-hidden text-surface-900 dark:text-[#f4f4fe] bg-gradient-to-b from-white to-surface-50
                dark:bg-[radial-gradient(130%_95%_at_15%_-10%,#332a86_0%,#1c1550_34%,#0c0930_62%,#020103_100%)]"
         role="dialog" aria-modal="true" [attr.aria-label]="nom()">
      <div class="lueur-haute absolute pointer-events-none hidden dark:block" aria-hidden="true"></div>
      <div class="lueur-laterale absolute pointer-events-none hidden dark:block" aria-hidden="true"></div>

      <div class="relative z-[1] h-full flex flex-col">
        <div class="flex-none px-[18px] pt-5 flex items-center justify-between">
          <button type="button" (click)="minimiser.emit()" [attr.aria-label]="t().calls.minimize" class="rond border bg-white border-surface-200 text-surface-700
                  dark:bg-white/[0.09] dark:border-white/[0.08] dark:text-[#f4f4fe] dark:backdrop-blur-[10px]">
            <lucide-icon [img]="ChevronDown" [size]="20" aria-hidden="true"></lucide-icon>
          </button>
          <div class="flex items-center gap-2 rounded-full py-[7px] pl-[11px] pr-3.5 border bg-white border-surface-200 dark:bg-white/[0.07] dark:border-white/[0.08] dark:backdrop-blur-[10px]">
            <span class="w-[7px] h-[7px] rounded-full" [style.background]="connectee() ? '#16a34a' : '#fbbf24'" aria-hidden="true"></span>
            <span class="text-xs font-bold tracking-[0.03em] tabular-nums text-surface-700 dark:text-[#e7e5fb]">{{ duree() }}</span>
          </div>
          <span class="w-11" aria-hidden="true"></span>
        </div>

        <div class="flex-none text-center px-6 pt-4">
          <div class="text-[21px] leading-[normal] font-extrabold tracking-[-0.01em]">{{ nom() }}</div>
          <div class="text-[12.5px] leading-[normal] mt-0.5 min-h-[18px] text-surface-500 dark:text-[#a5a1d6]" aria-live="polite">{{ statut() }}</div>
        </div>

        @if (message(); as m) {
          <div class="mx-5 mt-2 rounded-xl px-3.5 py-2 text-[12.5px] flex items-center justify-between gap-3 border bg-accent-50 border-accent-200 text-accent-800
                      dark:bg-[rgba(251,191,36,0.14)] dark:border-[rgba(251,191,36,0.3)] dark:text-[#fde68a]" role="alert">
            <span>{{ m }}</span>
            @if (salle.etat() === 'coupee' || salle.etat() === 'erreur') {
              <button type="button" (click)="salle.reessayer()" class="font-bold underline min-h-[44px]">{{ t().calls.retry }}</button>
            }
          </div>
        }

        <div class="flex-1 min-h-0 flex items-center justify-center px-5 pt-3.5 pb-1.5">
          <div class="relative w-full max-w-[320px] aspect-[3/4] max-h-full rounded-[28px] overflow-hidden border bg-surface-100 border-surface-200
                      dark:border-0 dark:bg-[linear-gradient(160deg,#2a2472_0%,#181346_55%,#0c0a22_100%)] dark:shadow-[0_30px_60px_-18px_rgba(0,0,0,0.7),0_0_0_1px_rgba(255,255,255,0.08)_inset,0_0_0_3px_rgba(129,140,248,0.18)]">
            @if (distant(); as d) {
              @if (d.videoTrack) {
                <app-piste-video [piste]="d.videoTrack" class="absolute inset-0" />
              } @else {
                <div class="absolute inset-0 flex flex-col items-center justify-center gap-3.5">
                  <div class="avatar w-[100px] h-[100px] rounded-full overflow-hidden flex items-center justify-center text-4xl font-bold border bg-white border-surface-200 text-surface-700
                              dark:border-0 dark:text-white" [style.--degrade]="d.degrade"><app-photo-appel [photo]="d.photo ?? photoInterlocuteur()" [nom]="d.nom" [initiales]="d.initiale" /></div>
                  @if (type() !== 'audio') {
                    <div class="flex items-center gap-1.5 text-[12.5px] font-semibold text-surface-600 dark:text-[#c7c3f5]">
                      <lucide-icon [img]="VideoOff" [size]="15" aria-hidden="true"></lucide-icon>
                      {{ t().calls.camera_off }}
                    </div>
                  }
                </div>
              }
              <div class="absolute bottom-3 left-3 flex items-center gap-1.5 rounded-full py-1.5 pl-2 pr-3 border bg-white/95 border-surface-200
                          dark:border-0 dark:bg-[rgba(5,4,13,0.55)] dark:backdrop-blur-[8px]">
                @if (!d.micro) {
                  <lucide-icon [img]="MicOff" [size]="14" class="text-danger-600 dark:text-[#fb7185]" [attr.aria-label]="t().calls.muted"></lucide-icon>
                }
                <span class="text-xs font-semibold">{{ d.nom }}</span>
              </div>
              <div class="absolute top-3 right-3 flex items-end gap-0.5 rounded-lg px-2 py-1.5 border bg-white/95 border-surface-200
                          dark:border-0 dark:bg-[rgba(5,4,13,0.55)] dark:backdrop-blur-[8px]"
                   role="img" [attr.aria-label]="t().calls.network_quality">
                @for (b of barres(d.qualite); track $index) {
                  <span class="w-[3px] rounded-[1px]" [style.height.px]="b.hauteur" [style.background]="b.allumee ? couleurQualite(d.qualite) : '#cbd5e1'"></span>
                }
              </div>
            } @else {
              <div class="absolute inset-0 flex flex-col items-center justify-center gap-3.5">
                <div class="avatar w-[100px] h-[100px] rounded-full overflow-hidden flex items-center justify-center text-4xl font-bold border bg-white border-surface-200 text-surface-700
                            dark:border-0 dark:text-white" [style.--degrade]="degradeInterlocuteur()"><app-photo-appel [photo]="photoInterlocuteur()" [nom]="nom()" [initiales]="initialeInterlocuteur()" /></div>
              </div>
            }

            @if (moi(); as m) {
              <div class="absolute bottom-3.5 right-3.5 w-20 h-[106px] rounded-2xl overflow-hidden border-2 border-white bg-primary-50 shadow-[0_6px_18px_rgba(15,23,42,0.18)]
                          dark:border-0 dark:bg-[linear-gradient(160deg,#4338ca,#1e1b4b)] dark:shadow-[0_14px_24px_-8px_rgba(0,0,0,0.65),0_0_0_2px_rgba(255,255,255,0.16)]">
                @if (m.videoTrack) {
                  <app-piste-video [piste]="m.videoTrack" [miroir]="true" class="absolute inset-0" />
                  <span class="absolute inset-x-0 bottom-2 text-center text-[11px] font-bold text-surface-600 dark:text-white/90">{{ t().calls.you }}</span>
                } @else {
                  <div class="absolute inset-0 flex items-center justify-center text-xs font-bold text-primary-800 dark:text-[#e7e5fb]">{{ t().calls.you }}</div>
                }
                @if (!m.micro) {
                  <div class="absolute bottom-[5px] right-[5px] w-4 h-4 rounded-full flex items-center justify-center bg-white/90 dark:bg-black/50">
                    <lucide-icon [img]="MicOff" [size]="10" class="text-danger-600 dark:text-[#fb7185]" [attr.aria-label]="t().calls.muted"></lucide-icon>
                  </div>
                }
              </div>
            }
          </div>
        </div>

        <app-barre-controles-appel (ajouter)="ajouter.emit()" (raccrocher)="raccrocher.emit()" />
      </div>
    </div>
  `,
  styles: [
    `
      .rond { width:44px; height:44px; border-radius:9999px; display:flex; align-items:center; justify-content:center; cursor:pointer; }
      .lueur-haute { top:-130px; right:-100px; width:360px; height:360px; border-radius:9999px;
        background:radial-gradient(circle,rgba(129,140,248,0.34) 0%,rgba(129,140,248,0) 70%); filter:blur(4px); animation:derive-1 16s ease-in-out infinite; }
      .lueur-laterale { top:220px; left:-120px; width:300px; height:300px; border-radius:9999px;
        background:radial-gradient(circle,rgba(56,189,248,0.16) 0%,rgba(56,189,248,0) 70%); filter:blur(6px); animation:derive-2 13s ease-in-out infinite; }
      :host-context(.dark) .avatar { background:var(--degrade); animation:pulse-avatar 2.4s ease-in-out infinite; box-shadow:0 0 0 6px rgba(129,140,248,0.16),0 18px 34px -10px rgba(67,56,202,0.6); }
      @keyframes pulse-avatar { 0%,100% { box-shadow:0 0 0 0 rgba(129,140,248,0.45); } 50% { box-shadow:0 0 0 12px rgba(129,140,248,0); } }
      @keyframes derive-1 { 0%,100% { transform:translate(0,0) scale(1); } 50% { transform:translate(-18px,14px) scale(1.06); } }
      @keyframes derive-2 { 0%,100% { transform:translate(0,0); } 50% { transform:translate(16px,-10px); } }
      @media (prefers-reduced-motion: reduce) { .lueur-haute, .lueur-laterale, .avatar { animation:none; } }
    `,
  ],
})
export class EcranAppelActifComponent {
  /** L'interlocuteur de l'appel. */
  readonly nom = input.required<string>();
  readonly duree = input.required<string>();
  readonly type = input.required<CallKind>();
  /** Tous les participants de la salle, soi-meme en tete (voir `construireTuiles`). */
  readonly tuiles = input.required<TuileAppel[]>();
  readonly initialeInterlocuteur = input.required<string>();
  readonly degradeInterlocuteur = input.required<string>();
  /** Photo de profil de l'interlocuteur ; ses initiales a defaut. */
  readonly photoInterlocuteur = input<string | null | undefined>();

  readonly minimiser = output<void>();
  readonly ajouter = output<void>();
  readonly raccrocher = output<void>();

  readonly salle = inject(SalleAppel);
  readonly t = inject(TranslationService).t;

  readonly ChevronDown = ChevronDown;
  readonly VideoOff = VideoOff;
  readonly MicOff = MicOff;

  readonly moi = computed(() => this.tuiles().find((p) => p.moi));
  readonly distant = computed(() => this.tuiles().find((p) => !p.moi));
  readonly connectee = computed(() => this.salle.etat() === 'connectee');

  /** Sous le nom : ce que la salle sait vraiment (qui parle, ou ou en est la connexion). */
  readonly statut = computed(() => {
    const c = this.t().calls;
    switch (this.salle.etat()) {
      case 'connexion':
        return c.connecting;
      case 'reconnexion':
        return c.reconnecting;
      default:
        return this.distant()?.parle ? c.speaking_now : '';
    }
  });

  /** Bandeau d'avertissement : coupure, echec, ou peripherique refuse. */
  readonly message = computed(() => {
    const c = this.t().calls;
    switch (this.salle.etat()) {
      case 'coupee':
        return c.connection_lost;
      case 'erreur':
        return c.connection_error;
      default:
        break;
    }
    const a = this.salle.avertissement();
    return a === 'micro_refuse' ? c.mic_denied : a === 'camera_refusee' ? c.camera_denied : null;
  });

  /** Trois barres : combien sont allumees selon la qualite. */
  barres(qualite: string): { hauteur: number; allumee: boolean }[] {
    const niveau = qualite === 'excellent' ? 3 : qualite === 'good' ? 2 : qualite === 'poor' ? 1 : 0;
    return [6, 9, 12].map((hauteur, i) => ({ hauteur, allumee: i < niveau }));
  }

  couleurQualite(qualite: string): string {
    return qualite === 'excellent' || qualite === 'good' ? '#4ade80' : qualite === 'poor' ? '#fbbf24' : '#fb7185';
  }
}
